<template>
  <v-container class="profile-view">
    <v-row justify="center">
      <v-col cols="12" md="8">
        <h1 class="text-h4 font-weight-bold mb-6 text-center">Profile</h1>

        <!-- Show Google User Info if logged in -->
        <v-card v-if="$store.getters.isAuthenticated" class="mb-6">
          <v-card-title>
            <v-icon icon="mdi-google" class="mr-2" />
            Google Account
          </v-card-title>
          <v-card-text class="text-center">
            <v-avatar size="80" class="mb-4">
              <v-img
                v-if="$store.getters.currentUser?.photoURL"
                :src="$store.getters.currentUser.photoURL"
                alt="Google Avatar"
              />
              <v-icon v-else icon="mdi-account" size="40" />
            </v-avatar>
            <div class="text-h6 font-weight-medium">
              {{ $store.getters.currentUser?.displayName }}
            </div>
            <div class="text-body-2 text-medium-emphasis">
              {{ $store.getters.currentUser?.email }}
            </div>
          </v-card-text>
        </v-card>

        <!-- Telegram Integration Section -->
        <v-card>
          <v-card-title>
            <v-icon icon="mdi-telegram" class="mr-2" />
            Telegram Integration
          </v-card-title>
          <v-card-text>
            <div v-if="telegramUser" class="text-center">
              <v-alert type="success" class="mb-4">
                <template v-slot:title>Connected Successfully!</template>
                Your Telegram account is now linked to receive notifications.
              </v-alert>

              <v-avatar size="64" class="mb-4">
                <v-img :src="telegramUser.photo_url" alt="Telegram Avatar" />
              </v-avatar>
              <div class="text-h6 mb-2">
                Welcome, <strong>{{ telegramUser.first_name }}</strong
                >!
              </div>
              <div class="text-body-2 text-medium-emphasis mb-4">
                @{{ telegramUser.username }}
              </div>

              <v-btn
                color="error"
                variant="outlined"
                prepend-icon="mdi-unlink"
                @click="disconnectTelegram"
              >
                Disconnect Telegram
              </v-btn>
            </div>

            <div v-else class="text-center">
              <v-alert type="info" class="mb-4">
                <template v-slot:title>Connect Your Telegram</template>
                Link your Telegram account to receive notifications about your
                investments and market updates.
              </v-alert>
              <TelegramLogin
                bot-name="DipSipLogin_bot"
                :requestAccess="true"
                @telegram-auth="onTelegramAuth"
              />
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loading overlay -->
    <v-overlay v-model="isLoading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64" color="primary" />
    </v-overlay>

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import TelegramLogin from "../components/TelegramLogin.vue";

const store = useStore();
const telegramUser = ref(null);
const isLoading = ref(false);
const snackbar = ref({
  show: false,
  message: "",
  color: "info",
  timeout: 3000,
});

// Computed property to get current user from Vuex store
const currentUser = computed(() => store.getters.currentUser);

onMounted(() => {
  const saved = localStorage.getItem("telegramUser");
  if (saved) {
    try {
      telegramUser.value = JSON.parse(saved);
    } catch (error) {
      console.error("Error parsing saved telegram user:", error);
      localStorage.removeItem("telegramUser");
    }
  }
});

async function onTelegramAuth(user) {
  console.log("✅ Telegram user authenticated:", user);
  isLoading.value = true;

  try {
    telegramUser.value = user;
    localStorage.setItem("telegramUser", JSON.stringify(user));

    const userEmail = currentUser.value?.email;
    if (!userEmail) {
      throw new Error("No user email found. Please login with Google first.");
    }

    // Use the dedicated Telegram auth endpoint
    await updateTelegramIdViaAPI(userEmail, user);

    showSnackbar("Telegram account connected successfully!", "success");
  } catch (error) {
    console.error("❌ Telegram auth failed:", error);
    showSnackbar(
      error.message || "Failed to connect Telegram account",
      "error"
    );
    // Clean up on error
    telegramUser.value = null;
    localStorage.removeItem("telegramUser");
  } finally {
    isLoading.value = false;
  }
}

async function updateTelegramIdViaAPI(email, telegramUser) {
  const token =
    localStorage.getItem("AUTH_TOKEN") || localStorage.getItem("token");

  try {
    const response = await fetch("/api/telegram/auth", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      body: JSON.stringify({
        userTG: telegramUser,
        userGoogle: {
          email: email,
        },
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }

    console.log("✅ Telegram ID updated via API:", data);
    return data;
  } catch (error) {
    console.error("API update failed:", error);
    throw error;
  }
}

function disconnectTelegram() {
  telegramUser.value = null;
  localStorage.removeItem("telegramUser");
  showSnackbar("Telegram account disconnected", "info");
}

function showSnackbar(message, color = "info") {
  snackbar.value = {
    show: true,
    message,
    color,
    timeout: 3000,
  };
}
</script>

<style scoped>
.profile-view {
  max-width: 800px;
  margin: 0 auto;
}
</style>
